import React from "react";
import {
  CoherencePanel,
  CoherencePanelSize,
} from "@coherence-design-system/controls";
import { CoherencePanelStyles } from "../../shared/style/CommonStyleObjects";
import PanelActionButtons from "../../shared/components/PanelActionButtons";
import { Link, MessageBar, MessageBarType } from "@fluentui/react";
import { BulkRemovalPanelProps } from "../../shared/models/BulkRemovalPanelProps.model";
import BulkRemovalForm from "./BulkRemovalForm";
import { isMercuryTenant } from "../../shared/models/Constants";

interface IBulkRemovalPanelProps {
  bulkRemovalPanelProps: BulkRemovalPanelProps;
}

class BulkRemovalPanel extends React.Component<IBulkRemovalPanelProps> {
  isReadyToSubmit = (): boolean => {
    const {
      file,
      reasonForRemoval,
      reasonForRemovalCustomText,
      fileValidationErrors: validationError,
      saveInProgress,
    } = this.props.bulkRemovalPanelProps;
    if (
      file === null ||
      reasonForRemoval === "" ||
      reasonForRemoval === null ||
      (reasonForRemoval === "OTHER" && reasonForRemovalCustomText === "") ||
      reasonForRemovalCustomText === null ||
      (validationError && validationError.length > 0) ||
      saveInProgress
    ) {
      return false;
    }
    return true;
  };
  
  onResetPanel = (): void => {
    this.props.bulkRemovalPanelProps.closePanel();
  };

  downloadLink = (text: string) => {
    const { downloadFile } = this.props.bulkRemovalPanelProps;
    return (
      <Link
        href=""
        target="_blank"
        onClick={(e) => {
          e.preventDefault();
          downloadFile();
        }}
      >
        {text}
      </Link>
    );
  };

  requestFileRulesMessage = (tenantName: string) => {
    if (isMercuryTenant(tenantName)) {
      return (
        <span>
          <br />
          <b>Role:</b> The Role field is required. Eligible roles for bulk removal: Basic Access, Informa, P&L and P&L Plan, Trial Balance & Statutory, and Support.
          Roles that have Permission Scopes (e.g. P&L and P&L Plan, Trial Balance & Statutory) must have a Permission Scope included in the request. 
          <br /><br />
          <b>Permission Scope:</b> Eligible permission scopes for bulk removal: <i>Geography</i>, <i>Functional/Exec Org Summary</i>, and <i>Consolidated GAAP</i> for P&L and P&L Plan role, and <i>All Companies</i> and <i>Specific Companies</i> for Trial Balance & Statutory role.
          <br /><br />
          This feature does not currently support fine-grained removal of users from specific Geographies, Functional/ Exec Org Summaries,
          or Companies. Users instead will be removed from all role assignments under the specified Permission Scope. 
          Please reference the bulk removal template for the correct format.
        </span>
      );
    }
    else {
      return (
        <span>
          The Role and BusinessGroup fields are optional. If a role is not
          provided, the user will be removed from all roles in {tenantName}. If
          a role is not provided but a business group is provided, the user will
          be removed from all roles in the specified business group.
        </span>
      )
    }
  };

  render() {
    const {
      closePanel,
      tenantName,
      onSubmit,
      fileErrorMessage,
      saveInProgress,
      fileValidationErrors: validationError
    } = this.props.bulkRemovalPanelProps;

    return (
      <CoherencePanel
        title="Bulk Removal Request"
        titleText="Bulk Removal Request"
        panelSize={CoherencePanelSize.medium}
        isOpen
        onDismiss={this.onResetPanel}
        styles={CoherencePanelStyles}
        hasCloseButton
        closeButtonAriaLabel="Close Bulk Removal Panel"
        onRenderFooter={(): JSX.Element => (
          <PanelActionButtons
            mutationLoading={saveInProgress}
            disabled={!this.isReadyToSubmit()}
            closePanel={closePanel}
            handleSubmit={onSubmit}
            saveLabel="Submit Request"
            saveTitle="Submit Request"
            cancelLabel="Cancel Request"
            cancelTitle="Cancel Request"
          />
        )}
      >
        <MessageBar
          messageBarType={MessageBarType.info}
          styles={{ root: { padding: 5, marginBottom: 20 } }}
        >
          Upload an XLSX file containing the list of users to be removed. You
          may use {this.downloadLink("this template")} for your bulk removal
          import.
          <br />
          <br />
          <b>UserUPN:</b> The UserUPN field must be in the following format:{" "}
          <i>user@domain.com</i>.
          <br />
          {this.requestFileRulesMessage(tenantName)}
          <br />
          <br />
          Imports are limited to no more than 5,000 rows at a time to prevent
          performance issues. Excel files with encrypted Data Sensitivity tags are not supported and
          will fail to import. Make sure Data Sensitivity tags are set to "General" before importing.
        </MessageBar>

        {validationError && validationError.length > 0 && (
          <MessageBar
            messageBarType={MessageBarType.error}
            styles={{ root: { padding: 5, marginBottom: 20 } }}
          >
            {validationError.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </MessageBar>
        )}
        <BulkRemovalForm
          bulkRemovalPanelProps={this.props.bulkRemovalPanelProps}
        />
      </CoherencePanel>
    );
  }
}

export default BulkRemovalPanel;
